/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import { useApi } from "../../../context/api.js";

import NOTIFICATIONS_ICONS from "../../../assets/icons/notifications-icons.js";
import useNotifications from "../../../context/notifications-actions.js";
import Link from "@mui/material/Link";
import MDTypography from "../../../components/MDTypography";
import { formatDate } from "../../../utils/date.js";

function DashboardNavbar({ absolute, light, isMini, onSearch }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const api = useApi();
  const notificationActions = useNotifications();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [openNotificationsMenu, setOpenNotificationsMenu] = useState(false);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [latestNotifications, setLatestNotifications] = useState(null);
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  useEffect(() => {
    api.getNotificationsCount().then((res) => setNotificationsCount(res.unread || 0));
  }, []);

  useEffect(() => {
    if (openNotificationsMenu) {
      setLatestNotifications(null);
      api.getLatestNotifications().then((res) => {
        setLatestNotifications(res || []);
      });
    }
  }, [openNotificationsMenu]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);
  const handleOpenNotificationsMenu = (event) => setOpenNotificationsMenu(event.currentTarget);
  const handleCloseNotificationsMenu = () => setOpenNotificationsMenu(false);

  // Render menu
  const renderProfileMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        icon={<Icon>account_circle</Icon>}
        title="Modifier mon profil"
        onClick={() => navigate("/profile")}
        disabled
      />
      <Divider />
      <NotificationItem
        icon={<Icon>logout</Icon>}
        title="Se déconnecter"
        onClick={() => api.logout()}
      />
    </Menu>
  );

  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const renderNotificationsMenu = () => (
    <Menu
      anchorEl={openNotificationsMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openNotificationsMenu)}
      onClose={handleCloseNotificationsMenu}
      sx={{ mt: 2 }}
    >
      {latestNotifications === null && (
        <MenuItem>
          <CircularProgress color="info" />
        </MenuItem>
      )}
      {latestNotifications !== null && !latestNotifications.length && (
        <MenuItem>Pas de nouvelles notifications.</MenuItem>
      )}
      {latestNotifications !== null &&
        !!latestNotifications.length &&
        latestNotifications.map((n) => {
          const Icon = NOTIFICATIONS_ICONS[n.type] || NOTIFICATIONS_ICONS["other"];

          return (
            <NotificationItem
              key={n.id}
              date={formatDate(
                n.created,
                new Date(n.created) > startOfDay ? "Aujourd'hui à hh:mm" : "Le dd/MM/yyyy à hh:mm"
              )}
              icon={<Icon />}
              title={<span style={{ fontWeight: n.viewed ? 400 : 700 }}>{n.subject}</span>}
              onClick={() => {
                if (!n.viewed) {
                  api.markAsViewed(n.id).then((res) => {
                    if (res) {
                      setLatestNotifications((notifs) =>
                        notifs.map((notif) =>
                          notif.id === n.id ? { ...notif, viewed: true } : notif
                        )
                      );
                    }
                  });
                }

                if (notificationActions[n.type]) {
                  setOpenNotificationsMenu(null);
                  notificationActions[n.type](n.params);
                }
              }}
            />
          );
        })}
      <MenuItem sx={{ justifyContent: "center" }}>
        <MDBox component={Link} py={0.5} lineHeight={1} href={"/notifications"}>
          <MDTypography variant="button" fontWeight="regular">
            Voir toutes les notifications
          </MDTypography>
        </MDBox>
      </MenuItem>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const handleStartSearch = (e) => {
    if (e.keyCode === 13) {
      onSearch(e.currentTarget.value || "");
    }
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {onSearch && (
              <MDBox pr={1}>
                <MDInput label="Rechercher..." InputProps={{ onKeyUp: handleStartSearch }} />
              </MDBox>
            )}
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenProfileMenu}
              >
                <Tooltip title="Mon profil">
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </Tooltip>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Tooltip title="Style de l'interface">
                  <Icon sx={iconsStyle}>settings</Icon>
                </Tooltip>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenNotificationsMenu}
              >
                <Tooltip title="Messages">
                  {/* TODO : Manage notifications pull (setInterval) */}
                  <Badge badgeContent={notificationsCount || null} color="primary">
                    <Icon sx={iconsStyle}>notifications</Icon>
                  </Badge>
                </Tooltip>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Tooltip title="Ouvrir le menu">
                  <Icon sx={iconsStyle} fontSize="medium">
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </Tooltip>
              </IconButton>
              {renderProfileMenu()}
              {renderNotificationsMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  onSearch: PropTypes.func,
};

export default DashboardNavbar;
